/**
 * [INPUT]: 依赖 supabaseAdmin、uuid
 * [OUTPUT]: 对外提供 wrap、createService、fetchAllPaginated（PostgREST 1000 硬上限循环）
 * [POS]: services/supabase 通用 CRUD 基座，被各表 service / dashboard 消费
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */

import { v4 as uuidv4 } from 'uuid'
import { supabaseAdmin } from './index'

export const wrap = {
  success: (data, msg = '操作成功') => ({
    code: 200,
    msg,
    data,
    requestId: uuidv4(),
  }),
  page: (list, count, pageIndex, pageSize) => ({
    code: 200,
    msg: '查询成功',
    data: { list, count, pageIndex, pageSize },
    requestId: uuidv4(),
  }),
  error: (msg, code = 500) => ({
    code,
    msg,
    data: null,
    requestId: uuidv4(),
  }),
}

const PAGE_SIZE = 1000

/** PostgREST max-rows=1000：用 queryFactory 每页拉满，避免静默截断 */
export async function fetchAllPaginated(queryFactory) {
  const all = []
  let from = 0
  for (;;) {
    const to = from + PAGE_SIZE - 1
    const { data, error } = await queryFactory().range(from, to)
    if (error) throw error
    const chunk = data || []
    all.push(...chunk)
    if (chunk.length < PAGE_SIZE) break
    from += PAGE_SIZE
  }
  return all
}

export function createService(tableName) {
  const db = () => {
    if (!supabaseAdmin) throw new Error('supabaseAdmin 未配置')
    return supabaseAdmin.from(tableName)
  }

  return {
    async list({
      page = 1,
      pageSize = 20,
      filters = {},
      orderBy = 'created_at',
      ascending = false,
      selectFields = '*',
    } = {}) {
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1
      let query = db().select(selectFields, { count: 'exact' })

      Object.entries(filters).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return
        if (typeof value === 'object' && value.op) {
          query = query[value.op](key, value.value)
        } else if (typeof value === 'string' && value.includes('%')) {
          query = query.ilike(key, value)
        } else if (typeof value === 'string' && ['name', 'nickname', 'title', 'phone', 'industry', 'occupation'].includes(key)) {
          query = query.ilike(key, `%${value}%`)
        } else {
          query = query.eq(key, value)
        }
      })

      query = query.range(from, to).order(orderBy, { ascending })
      const { data, error, count } = await query
      if (error) throw error
      return wrap.page(data, count, page, pageSize)
    },

    async get(id, selectFields = '*') {
      const { data, error } = await db().select(selectFields).eq('id', id).single()
      if (error) throw error
      return wrap.success(data)
    },

    async count(filters = {}) {
      let query = db().select('id', { count: 'exact', head: true })
      Object.entries(filters).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return
        query = query.eq(key, value)
      })
      const { count, error } = await query
      if (error) throw error
      return wrap.success(count ?? 0)
    },

    raw: () => db(),
    wrap,
  }
}

export default createService
